import React from "react";
import { Container, Accordion, Form } from "react-bootstrap";
import "../../styles/General.css";
import SEO from "../utils/SEO";

const topics = [
  {
    title: "General Info",
    content: [
      {
        question: "What is Sârv?",
        answer:
          "Sârv is an app designed to help you receive personalized holistic healthcare recommendations that fit your body and health goals. All recommendations are based on natural and medicinal herbs",
      },
      {
        question:
          "How is Sârv's recommendation different from other online platforms?",
        answer:
          "Sârv first understands how your body works by asking a few questions, then creates a body profile describing your characteristics and health goals or concerns. It then recommends natural remedies based on medicinal plants to help you achieve your health goals. \n\n Our knowledge base has been created from hundreds of scientific books and peer-reviewed articles written by experts in the fields of phytotherapy, medicinal plants, naturopathy, and nutrition. ",
      },
      {
        question: "What does Sârv premium membership include?",
        answer:
          "Sarv premium membership offers a comprehensive educational database on medicinal plants and over 300 natural remedies for common ailments. It also provides personalized health recommendations tailored to the user's symptoms and biology.",
      },
      {
        question: "Is my data secure? What is your privacy policy?",
        answer:
          "We take data security and your privacy seriously and your information will be stored securely. Our full privacy policy can be found at privacy policy section.",
      },
      {
        question: "Is Sârv FDA approved?",
        answer:
          "No, Sârv is not meant to diagnose or treat any medical conditions. It is an app that is meant for general wellness purposes only.",
      },
      {
        question: "How much does the Sârv program cost?",
        answer: [
          "We offer two flexible membership options:",
          "Weekly Subscription: $9.99 per week - Billed weekly and auto-renews every 7 days.",
          "Annual Membership: $69.99 per year (save over 85% vs weekly) - Includes a 3-day free trial, then billed annually.",
          "Payment is charged automatically at the end of your billing period and auto-renews unless you cancel your subscription.",
        ],
      },
      {
        question: "Is there a free trial?",
        answer:
          "Yes! Our Annual Membership ($69.99/year) includes a 3-day free trial. You can explore all premium features risk-free. If you cancel within the 3-day trial period, you won't be charged. The Weekly Subscription ($9.99/week) does not include a free trial and billing begins immediately upon signup.",
      },
      {
        question: "When will I be charged?",
        answer:
          "For the Annual Membership, you'll be charged after your 3-day free trial ends. For the Weekly Subscription, you'll be charged immediately upon signup. All subscriptions auto-renew at the end of each billing period unless cancelled.",
      },
      {
        question: "Can I switch between subscription plans?",
        answer:
          "Yes, you can upgrade or downgrade your subscription plan at any time through your account settings. Changes will take effect at the start of your next billing cycle. Please note that switching plans does not extend your current subscription or provide refunds for unused time.",
      },
    ],
  },
  {
    title: "Account",
    content: [
      {
        question: "Do I need a smartphone to access Sârv?",
        answer:
          "Yes. The Sârv app is currently available on both iOS (iPhone or iPad with iOS version 13.2 or later) and Android devices (Samsung, LG, Motorola etc. with Android 8.0 or later).",
      },
      {
        question: "What do I do if I forgot my login credentials?",
        answer:
          "We're sorry about that! Please contact us at support@joinsarv.com, and we will assist you with credential retrieval.",
      },
    ],
  },
  {
    title: "Partnerships",
    content: [
      {
        question: "I would love to partner up with Sârv! Who can I contact?",
        answer:
          "Please forward any affiliate/partnership requests to support@joinsarv.com  or fill out this <a href='/affiliate-request'>form</a>  and our team will get back to you.",
        isHtml: true,
      },
      {
        question:
          "I'm a licensed practitioner. How can I provide services using Sârv?",
        answer:
          "Please start the application process by clicking <a href='/careers'>here</a>.",
        isHtml: true,
      },
    ],
  },
  {
    title: "Cancellations & Refunds",
    content: [
      {
        question: "What is your returns & refund policy?",
        answer:
          "For Annual Memberships: You can cancel within the 3-day free trial period for a full refund (no charges will be made). After the free trial ends, all payments are non-refundable. For Weekly Subscriptions: All payments are non-refundable from the moment of purchase. We do not offer partial refunds for early cancellations or unused portions of your subscription period.",
      },
      {
        question: "How do I cancel my Sârv subscription?",
        answer:
          "You can cancel your membership at any time through the Sârv app. Go to 'Settings' in the menu, then select the 'Account' section where you'll find subscription management options. Your cancellation will take effect at the end of your current billing period, and you'll retain access to premium features until then.",
      },
      {
        question: "What happens after I cancel my subscription?",
        answer:
          "After cancellation, you'll continue to have access to all premium features until the end of your current billing period. Once your subscription expires, you'll lose access to personalized recommendations and premium content, but your account and data will remain saved. You can reactivate your subscription at any time.",
      },
      {
        question:
          "Will I receive a refund if I cancel before my subscription ends?",
        answer:
          "No. We do not offer partial refunds or prorated credits for cancellations made during an active subscription period. You will retain access to all premium features until the end of your paid subscription period.",
      },
      {
        question: "How do I update my subscription?",
        answer:
          "You can manage your membership in the Sârv app. In the menu, tap on 'Settings', and you'll be able to update your subscription from the 'Account' section. Changes will take effect at the start of your next billing cycle.",
      },
      {
        question: "How can I give feedback or raise a complaint?",
        answer:
          "We're sorry to hear about any disappointment, and we're eager to sort it out. Thank you for taking the time to talk to us. We want to ensure that we're aware of any issues so we can correct them, not just for you, but for others, too. We'll do our best not to make the same mistake again. Please email us anytime at support@joinsarv.com or reach out to us via the chat and select the complaints option.",
      },
    ],
  },
  {
    title: "Technical & Features",
    content: [
      {
        question: "What payment methods do you accept?",
        answer:
          "We accept all major credit and debit cards including Visa, Mastercard, American Express, and Discover. All payments are processed securely through our payment provider.",
      },
      {
        question: "How do the personalized recommendations work?",
        answer:
          "After completing your health profile assessment, our system analyzes your responses using our extensive database of medicinal plants and natural remedies. We match your specific symptoms, health goals, and body characteristics with evidence-based natural solutions. Recommendations are continuously refined as you provide feedback and update your health information.",
      },
      {
        question: "Can I access Sârv on multiple devices?",
        answer:
          "Yes! You can access your Sârv account on multiple devices using the same login credentials. Your health profile and recommendations will sync across all devices.",
      },
      {
        question: "What if I'm experiencing technical issues?",
        answer:
          "If you're experiencing any technical difficulties, please try these steps first: 1) Ensure your app is updated to the latest version, 2) Try logging out and back in, 3) Restart your device. If issues persist, please contact our support team at support@joinsarv.com with details about the problem and your device information.",
      },
      {
        question: "How often is your content updated?",
        answer:
          "Our database of medicinal plants, natural remedies, and health information is regularly reviewed and updated by our team of experts. We continuously add new research findings, remedy recommendations, and educational content to ensure you have access to the most current and accurate information.",
      },
      {
        question: "Can I save or bookmark my favorite remedies?",
        answer:
          "Yes! Premium members can save their favorite remedies, articles, and recommendations for quick access later. Simply tap the bookmark icon on any content you want to save.",
      },
    ],
  },
];

const faqSchemaData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: topics.flatMap((topic) =>
    topic.content
      .filter((item) => !item.isHtml)
      .map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: Array.isArray(item.answer)
            ? item.answer.join(" ")
            : item.answer,
        },
      }))
  ),
};

const FAQ = () => {
  return (
    <div className="progress-container">
      <SEO
        title="FAQ - Sarv Holistic Healthcare"
        description="Frequently asked questions about Sarv's personalized holistic health recommendations, subscription plans ($9.99/week or $69.99/year with free trial), cancellation policy, and more."
        canonicalPath="/faq"
        keywords="holistic health FAQ, herbal medicine questions, personalized health app FAQ, Sarv subscription, natural remedies app help"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchemaData) }}
      />
      <Container className="d-flex align-items-center justify-content-center">
        <div
          className="w-100"
          style={{
            maxWidth: "1000px",
            paddingTop: "60px",
            paddingBottom: "50px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <h1 className="faq-title">Frequently asked questions</h1>
          </div>

          <Form>
            {topics.map((topic, topicIndex) => (
              <Accordion
                defaultActiveKey="0"
                key={topic.title}
                className="my-accordion"
              >
                <Accordion.Item
                  eventKey={topicIndex.toString()}
                  className="my-accordion-item"
                  style={{ border: "none" }}
                >
                  <Accordion.Header>
                    <div className="email-subscription-title">
                      {topic.title}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    {topic.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <h5>{item.question}</h5>
                        {Array.isArray(item.answer) ? (
                          <ul>
                            {item.answer.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        ) : item.isHtml ? (
                          <div
                            dangerouslySetInnerHTML={{ __html: item.answer }}
                            style={{ paddingBottom: "15px" }}
                          ></div>
                        ) : (
                          <p>{item.answer}</p>
                        )}
                      </div>
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            ))}
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default FAQ;
